import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useQuery } from '@tanstack/react-query';
import { readReviewHtml, type OfficialCheatsheet } from './cheatsheetApi';

export function CheatsheetHtmlReview({
  sheet,
  fixed,
  fullscreen = false,
  paper = 'A3',
  colour = 'colour',
}: {
  sheet: OfficialCheatsheet;
  fixed: boolean;
  fullscreen?: boolean;
  paper?: 'A4' | 'A3';
  colour?: 'colour' | 'mono';
}) {
  const content = useQuery({
    queryKey: [
      'study-html-review',
      sheet.key,
      sheet.version,
      sheet.development_review ?? false,
      sheet.html?.sha256,
    ],
    queryFn: async () => {
      const value = await readReviewHtml(sheet);
      const bytes = new TextEncoder().encode(value.html);
      const hash = Array.from(
        new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)),
        (byte) => byte.toString(16).padStart(2, '0'),
      ).join('');
      if (
        hash !== value.sha256 ||
        (sheet.html && (hash !== sheet.html.sha256 || bytes.byteLength !== sheet.html.bytes))
      )
        throw new Error('The HTML reference could not be verified.');
      return value.html;
    },
    retry: false,
  });
  if (content.isPending) return <p role="status">Loading the complete HTML cheatsheet…</p>;
  if (content.isError)
    return (
      <div role="alert">
        <p>We couldn’t load this HTML reference.</p>
        <button
          type="button"
          className="btn-pill-ghost mt-3"
          onClick={() => void content.refetch()}
        >
          Try again
        </button>
      </div>
    );
  return (
    <VerifiedDocument
      html={content.data}
      title={`${sheet.title} · ${fixed ? `Fixed ${paper}` : 'Responsive HTML'}`}
      fixed={fixed}
      fullscreen={fullscreen}
      paper={paper}
      colour={colour}
    />
  );
}

/** Preserve the verified master, but never execute its scripts in the signed-in Study page. */
function VerifiedDocument({
  html,
  title,
  fixed,
  fullscreen,
  paper,
  colour,
}: {
  html: string;
  title: string;
  fixed: boolean;
  fullscreen: boolean;
  paper: 'A3' | 'A4';
  colour: 'colour' | 'mono';
}) {
  const host = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const shadow = element.shadowRoot ?? element.attachShadow({ mode: 'open' });
    const master = new DOMParser().parseFromString(html, 'text/html');
    const style = document.createElement('style');
    // Rewrite selector tokens only: declarations, embedded fonts and diagrams stay intact.
    style.textContent =
      [...master.querySelectorAll('style')]
        .map((node) => node.textContent ?? '')
        .join('\n')
        .replace(/(^|[{}])([^{}]+)\{/g, (rule, boundary: string, selector: string) =>
          selector.trim().startsWith('@')
            ? rule
            : `${boundary}${selector.replace(/:root/g, ':host').replace(/\b(?:html|body)\b/g, '.cheatsheet-document')}{`,
        ) +
      '\n:host{display:block;isolation:isolate;contain:layout style;} .cheatsheet-document{margin:0;min-width:0;} .cheatsheet-document,.sheet{background:var(--paper,#fff)!important}';
    const surface = document.createElement('div');
    surface.className = [
      'cheatsheet-document',
      master.body.className,
      fixed ? 'fixed' : 'responsive-mode',
      paper === 'A4' ? 'a4' : '',
      colour === 'mono' ? 'mono' : '',
    ].join(' ');
    surface.innerHTML = DOMPurify.sanitize(master.body.innerHTML, {
      FORBID_TAGS: [
        'script',
        'style',
        'iframe',
        'object',
        'embed',
        'form',
        'input',
        'button',
        'textarea',
        'select',
        'link',
        'meta',
        'base',
      ],
      FORBID_ATTR: ['srcdoc', 'autofocus'],
    });
    for (const link of surface.querySelectorAll('a')) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
    shadow.replaceChildren(style, surface);
    const fit = () => {
      if (!fixed) return;
      if (fullscreen && element.clientHeight > 0) {
        // Controls wrap on narrow screens; measure their real space before fitting the paper.
        element.style.height = `${Math.max(220, window.innerHeight - element.getBoundingClientRect().top - 24)}px`;
      }
      const width = ((paper === 'A4' ? 297 : 420) / 25.4) * 96;
      const height = ((paper === 'A4' ? 210 : 297) / 25.4) * 96;
      const scale = Math.max(
        0.05,
        Math.min((element.clientWidth - 32) / width, (element.clientHeight - 32) / height),
      );
      for (const frame of surface.querySelectorAll<HTMLElement>('.frame')) {
        frame.style.width = `${width * scale}px`;
        frame.style.height = `${height * scale}px`;
        const page = frame.querySelector<HTMLElement>('.sheet, .slide');
        if (page) {
          page.style.width = `${width}px`;
          page.style.height = `${height}px`;
          page.style.transformOrigin = 'top left';
          page.style.transform = `scale(${scale})`;
        }
      }
    };
    const observer = new ResizeObserver(fit);
    observer.observe(element);
    window.addEventListener('resize', fit);
    fit();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', fit);
      shadow.replaceChildren();
    };
  }, [html, fixed, paper, colour, fullscreen]);
  return (
    <div
      ref={host}
      role="document"
      title={title}
      aria-label={title}
      style={fixed && fullscreen ? { height: 'calc(100dvh - 250px)', minHeight: 220 } : undefined}
      className={`${fixed ? 'h-[75dvh] min-h-[500px] overflow-auto' : 'block'} w-full rounded-2xl border border-hairline bg-canvas-pure shadow-card-soft`}
    />
  );
}
